import sortBy from 'lodash/sortBy';
import { useCallback, useState } from 'react';

import {
  ACTIVITIES_COMPONENT_TYPE_COLUMNS,
  ACTIVITIES_COMPONENT_TYPE_ACTIVITY,
} from 'global/constants';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import ActivityBuilderZoomComponentModal from '../../activity-builder-zoom-component-modal';
import type { InteractiveLessonSectionFragment } from '../../lesson-builder.graphql.generated';
import { SectionOrSubSection } from '../../activity-builder.constants';
import {
  ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
  ComponentEventData,
  ComponentExpandModeType,
  pushComponentViewEvent,
} from '../../present/activity-builder-present-data-tracking.utils';
import { ComponentColumns } from '../component.constants';
import ComponentsViewWrapper from '../components-view-wrapper';
import ActivitySampleComponent from './activity-sample-components';
import ActivitySampleColumns from './activity-sample-columns';
import { getStyleProperties } from '../components.utils';
import {
  getAppliedColour,
  isBackgroundApplied,
  isBorderApplied,
} from '../shared/component-settings-borders.utils';
import type { Pluck } from 'lib/gql/pluck';

type Props = {
  section: SectionOrSubSection;
  contentId: number;
  eventTrackingData?: ComponentEventData;
};

const ActivitySampleView = ({
  section,
  contentId,
  eventTrackingData,
}: Props) => {
  const [zoomedComponent, setZoomedComponent] = useState<
    Pluck<InteractiveLessonSectionFragment, 'components'> | undefined
  >();

  const components = section?.components;
  const sortedComponents = sortBy(components, 'sort');

  const { isVisible, openModal, closeModal } = useSimpleModalState();

  const handleOpenModal = useCallback(
    (
      component: Pluck<InteractiveLessonSectionFragment, 'components'>,
      expandMode?: ComponentExpandModeType
    ) => {
      setZoomedComponent(component);
      openModal();
      pushComponentViewEvent({
        component,
        eventType: ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
        expandMode,
        eventTrackingData,
        contentId,
      });
    },
    [contentId, eventTrackingData, openModal]
  );

  if (!sortedComponents) return null;

  return (
    <div>
      {sortedComponents.map(component => {
        return component.type !== ACTIVITIES_COMPONENT_TYPE_ACTIVITY ? (
          <ComponentsViewWrapper
            key={component.id}
            type={component.type}
            id={component.id}
            colours={getAppliedColour(getStyleProperties(component))}
            showBorder={isBorderApplied(getStyleProperties(component))}
            showBackground={isBackgroundApplied(getStyleProperties(component))}
          >
            {component.type === ACTIVITIES_COMPONENT_TYPE_COLUMNS ? (
              <ActivitySampleColumns
                {...(component as ComponentColumns)}
                contentId={contentId}
                eventTrackingData={eventTrackingData}
                openModal={handleOpenModal}
              />
            ) : (
              <ActivitySampleComponent
                component={component}
                contentId={contentId}
                eventTrackingData={eventTrackingData}
                openModal={handleOpenModal}
              />
            )}
          </ComponentsViewWrapper>
        ) : null;
      })}

      {isVisible && zoomedComponent && (
        <ActivityBuilderZoomComponentModal
          component={zoomedComponent}
          closeModal={() => {
            setZoomedComponent(undefined);
            closeModal();
          }}
        />
      )}
    </div>
  );
};

export default ActivitySampleView;
